import { Button, Form, Skeleton, Table, Tooltip, Typography } from "antd";
import _cloneDeep from "lodash/cloneDeep";
import _find from "lodash/find";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import _reduce from "lodash/reduce";
import _set from "lodash/set";
import _unset from "lodash/unset";
import PropTypes from "prop-types";
import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AppModal from "../../components/AppModal/AppModal";
import CustomIcon from "../../components/CustomIcon/CustomIcon";
import TransferTable from "../../components/TransferTable/TransferTable";
import buildingService from "../../services/building.service";
import { refreshFeatureFlagsAfterBudSyncDisabled } from "../../store/actions/featureFlag.actions";
import { selectPropertySearchRedesignClientState } from "../../store/selectors/featureFlag.selector";
import { ERROR_MESSAGE, SUCCESS_MESSAGE } from "../../utils/constants";
import { getUnitLabel } from "../../utils/property.util";
import { to, toastMessage } from "../../utils/utils";
import {
  buildCharacteristicsRows,
  buildSyncRows,
  formatRefreshedSyncRows,
  getClearHeightScalarValue,
  getUnitClearHeightUpdates,
  getUnitSyncFields,
  normalizeUnitClearHeightPreviewRows,
  unitContainsCode,
} from "./characteristicsSync.util";
import "./Characteristics.scss";
import {
  formatSourceLeaseDate,
  getCurrentUnitFormDetails,
  hasSyncableUnitSourceDetails,
  getSourceLeaseDateOnlyValue,
  getSyncedSystemAvailabilityLeaseInfo,
  getSystemUnitAvailabilityDate,
} from "./unitForm.util";
import { findUnitDimensions, getDimensionAriaLabel, getSelectedDimensionBlock } from "./dimensions.util";

const { Title } = Typography;
const getLegacyCharacteristicRows = t => [
  { key: "mainBreakerSize", characteristic: t("UnitCharacteristics.MainBreakerSizeInAmps") },
  { key: "hvacWarehouse", characteristic: t("UnitCharacteristics.HVACEvaporativeCooling") },
  { key: "crossDock", characteristic: t("UnitCharacteristics.UnitCrossDock") },
  { key: "officeLightingType", characteristic: t("UnitCharacteristics.OfficeLightingType") },
  { key: "warehouseLightingType", characteristic: t("UnitCharacteristics.WarehouseLightingType") },
  { key: "dockHighDoors", characteristic: t("UnitCharacteristics.DockHighDoor") },
  { key: "clearHeight", characteristic: t("UnitCharacteristics.ClearHeight") },
  { key: "gradeLevelDoors", characteristic: t("UnitCharacteristics.GradeLevelDoors") },
  { key: "fireSuppressionSystem", characteristic: t("UnitCharacteristics.FireSuppressionSystem") },
  { key: "edgeOfDockLevelersCount", characteristic: t("PropertyCharacteristics.EdgeOfDockLevelersCount") },
  { key: "pitLevelersCount", characteristic: t("UnitCharacteristics.PitLevelersCount") },
  { key: "vehicleRestraintsCount", characteristic: t("UnitCharacteristics.VehicleRestraintsCount") },
  { key: "leaseEndDate", characteristic: t("UnitCharacteristics.LeaseEndDate") },
];
const legacyUnitSyncFieldIds = [
  "unitCode",
  "fireSuppressionSystem",
  "warehouseLightingType",
  "clearHeight",
  "gradeLevelDoors",
  "dockHighDoors",
  "mainBreakerSize",
  "leaseEndDate",
];
const legacyFormattedUnitFieldIds = ["fireSuppressionSystem", "warehouseLightingType", "leaseEndDate"];
const unitCharacteristicFieldIds = [
  "minimumClearHeight",
  "maximumClearHeight",
  "clearHeight",
  "gradeLevelDoors",
  "dockHighDoors",
  "edgeOfDockLevelersCount",
  "pitLevelersCount",
  "warehouseLightingType",
  "fireSuppressionSystem",
  "mainBreakerSize",
  "hvacWarehouse",
  "officeLightingType",
  "crossDock",
  "vehicleRestraintsCount",
];

const getLegacyBudValue = row => {
  if (legacyFormattedUnitFieldIds.includes(row.id)) return row.budDisplayValue;
  if (row.id === "clearHeight") return getClearHeightScalarValue(row.budValue);
  if (Object.prototype.hasOwnProperty.call(row, "applyValue")) return row.applyValue;
  return row.budValue;
};

const getLegacyDpmValue = row => {
  if (row.id === "clearHeight") return getClearHeightScalarValue(row.dpmValue);
  if (legacyFormattedUnitFieldIds.includes(row.id)) return row.dpmDisplayValue;
  return row.dpmValue;
};

const getFormSyncValue = value => (value === null ? undefined : value);
const getBudSyncErrorMessage = (err, fallback) => _get(err, "response.data.message", err?.message || fallback);
const getUnitFormSyncValue = row => {
  if (row.id === "clearHeight") return getFormSyncValue(getClearHeightScalarValue(row.updatedValue));
  return getFormSyncValue(row.updatedValue);
};

export const formatSavedClearHeight = (unit, buildingUnitOfMeasure, selectedUnitSystem, fallbackValue) => {
  const value = _get(unit, "characteristics.clearHeight", _get(unit, "clearHeight"));
  if (value === undefined || value === null || value === "") return fallbackValue;

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return fallbackValue;

  const sourceUOM = _get(unit, "characteristics.clearHeightUOM", _get(unit, "clearHeightUOM"));
  const normalizedUOM = `${
    sourceUOM && typeof sourceUOM === "object" ? sourceUOM.value || sourceUOM.text : sourceUOM || ""
  }`
    .trim()
    .toLowerCase();
  const hasExplicitUOM = sourceUOM !== undefined && sourceUOM !== null && sourceUOM !== "";
  const isLegacyHistUnit = Boolean(_get(unit, "effectiveFromDate"));
  let inferredUOM = normalizedUOM;
  if (!hasExplicitUOM) inferredUOM = isLegacyHistUnit ? buildingUnitOfMeasure : "us";
  const normalizedSourceUOM = `${
    inferredUOM && typeof inferredUOM === "object" ? inferredUOM.value || inferredUOM.text : inferredUOM || ""
  }`
    .trim()
    .toLowerCase();
  const isMetric = ["m", "meter", "meters", "metric"].includes(normalizedSourceUOM);
  const feetValue = isMetric ? numericValue / 0.3048 : numericValue;

  if (selectedUnitSystem === "metric") {
    const totalCentimeters = Math.round((isMetric ? numericValue : feetValue * 0.3048) * 100);
    return `${Math.floor(totalCentimeters / 100)} m ${totalCentimeters % 100} cm`;
  }

  const totalInches = Math.round(feetValue * 12);
  return `${Math.floor(totalInches / 12)} ft ${totalInches % 12} in`;
};

const formatClearHeightRange = (unit, buildingUnitOfMeasure, selectedUnitSystem) => {
  const minimum = formatSavedClearHeight(
    {
      ...unit,
      characteristics: {
        ..._get(unit, "characteristics", {}),
        clearHeight: _get(unit, "characteristics.minimumClearHeight", _get(unit, "minimumClearHeight")),
        clearHeightUOM: _get(unit, "characteristics.clearHeightUOM", _get(unit, "clearHeightUOM")),
      },
    },
    buildingUnitOfMeasure,
    selectedUnitSystem,
    null,
  );
  const maximum = formatSavedClearHeight(
    {
      ...unit,
      characteristics: {
        ..._get(unit, "characteristics", {}),
        clearHeight: _get(unit, "characteristics.maximumClearHeight", _get(unit, "maximumClearHeight")),
        clearHeightUOM: _get(unit, "characteristics.clearHeightUOM", _get(unit, "clearHeightUOM")),
      },
    },
    buildingUnitOfMeasure,
    selectedUnitSystem,
    null,
  );
  if (!minimum) return maximum;
  if (!maximum || minimum === maximum) return minimum;
  return `${minimum} – ${maximum}`;
};

const formatDimensionRange = (minimum, maximum, selectedUnitSystem) => {
  const min = getSelectedDimensionBlock(minimum, selectedUnitSystem)?.formatted;
  const max = getSelectedDimensionBlock(maximum, selectedUnitSystem)?.formatted;
  if (!min) return max;
  if (!max || min === max) return min;
  return `${min} – ${max}`;
};

const updateUnitCodeLabel = (unit, unitId, value) => {
  const nextLabel = getUnitLabel(value);
  return {
    ...unit,
    unitCode: Array.isArray(unit.unitCode)
      ? unit.unitCode.map(unitCode =>
          `${unitCode.value}` === `${unitId}` ? { ...unitCode, label: nextLabel } : unitCode,
        )
      : unit.unitCode,
    unitOptions: Array.isArray(unit.unitOptions)
      ? unit.unitOptions.map(unitOption =>
          `${unitOption.value}` === `${unitId}` ? { ...unitOption, label: nextLabel, unitCode: nextLabel } : unitOption,
        )
      : unit.unitOptions,
  };
};

const updateUnitCodeLabels = (unitInfo, unitId, value) =>
  unitInfo.map(unit => updateUnitCodeLabel(unit, unitId, value));

const applyUnitRowsToFormInfo = (currentUnitInfo, unitIndex, unitId, rows) => {
  let nextUnitInfo = _cloneDeep(currentUnitInfo || []);

  rows.forEach(row => {
    if (!row.path) return;
    if (row.path === "unitCode") {
      nextUnitInfo = updateUnitCodeLabels(nextUnitInfo, unitId, row.updatedValue);
      return;
    }
    if (row.id === "clearHeight" && row.updatedValue && typeof row.updatedValue === "object") {
      const minimum = getFormSyncValue(row.updatedValue.minimum);
      const maximum = getFormSyncValue(row.updatedValue.maximum);
      const scalar = getFormSyncValue(getClearHeightScalarValue(row.updatedValue));
      _set(nextUnitInfo, [unitIndex, "minimumClearHeight"], minimum);
      _set(nextUnitInfo, [unitIndex, "maximumClearHeight"], maximum);
      _set(nextUnitInfo, [unitIndex, "clearHeight"], scalar);
      _set(nextUnitInfo, [unitIndex, "characteristics", "minimumClearHeight"], minimum);
      _set(nextUnitInfo, [unitIndex, "characteristics", "maximumClearHeight"], maximum);
      _set(nextUnitInfo, [unitIndex, "characteristics", "clearHeight"], scalar);
      return;
    }
    const formPath = row.formPath || row.path;
    const syncValue = getUnitFormSyncValue(row);
    _set(nextUnitInfo, [unitIndex, ...formPath.split(".")], syncValue);
    if (syncValue === undefined && unitCharacteristicFieldIds.includes(row.id)) {
      _unset(nextUnitInfo, [unitIndex, "characteristics", row.id]);
    }
  });
  return nextUnitInfo;
};

const UnitCharacteristics = ({
  _unitCodes,
  selectedPropertyUnits,
  selectedProperty,
  form,
  notAvailable,
  index,
  viewOnlyMode,
  updateNotAvailableFields,
  dimensionDisplayProps,
}) => {
  const [isDifferenceModalOpen, setIsDifferenceModalOpen] = useState(false);
  const [codeDetail, setCodeDetail] = useState({});
  const [selected, setSelected] = useState({});
  const [selectedUnitCode, setSelectedUnitCode] = useState(undefined);
  const [syncLoading, setSyncLoading] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const propertySearchRedesignState = useSelector(selectPropertySearchRedesignClientState);
  const lastResolvedBudSyncState = useRef(propertySearchRedesignState === true);
  const isFeatureFlagResolved = propertySearchRedesignState !== undefined;
  if (isFeatureFlagResolved) lastResolvedBudSyncState.current = propertySearchRedesignState;
  const isBudSyncEnabled = isFeatureFlagResolved ? propertySearchRedesignState : lastResolvedBudSyncState.current;
  const getLeaseEndDateValue = value => formatSourceLeaseDate(value);
  const getUnitLeaseEndDate = unit => _get(unit, "leaseInfo.leaseEndDate");
  const syncFields = useMemo(() => {
    const fields = getUnitSyncFields(t, { property_search_redesign: { status: isBudSyncEnabled } });
    if (isBudSyncEnabled) return fields;
    return [
      ...fields,
      {
        key: "leaseEndDate",
        label: t("UnitCharacteristics.LeaseEndDate"),
        budPath: "leaseInfo.leaseEndDate",
        dpmPath: "leaseInfo.leaseEndDate",
        formPath: "leaseInfo.leaseEndDate",
        format: value => formatSourceLeaseDate(value),
        getApplyValue: getSourceLeaseDateOnlyValue,
      },
    ];
  }, [t, isBudSyncEnabled]);

  const _onCancel = () => {
    setCodeDetail({});
    setSelected({});
    setIsDifferenceModalOpen(false);
  };

  const _onClickSyncModal = (unitDetail, selectedUnit) => {
    if (!isFeatureFlagResolved) return;
    setCodeDetail(unitDetail);
    setSelected(selectedUnit);
    setSelectedUnitCode(unitDetail?.unitId || selectedUnit?.unitId);
    setIsDifferenceModalOpen(true);
  };

  const getSelectedDpmSource = () => {
    const source = {
      ...selected,
      ..._get(form.getFieldValue("unitInfo"), index, {}),
    };
    if (!Array.isArray(source.unitCode)) return source;
    const selectedCode = _find(source.unitCode, unitCode => `${unitCode.value}` === `${selectedUnitCode}`);
    return {
      ...source,
      unitCode: selectedCode ? selectedCode.label || selectedCode.value : selectedUnitCode,
    };
  };

  const getPreviewRows = async () => {
    if (!isFeatureFlagResolved || !isBudSyncEnabled) return [];
    setSyncLoading(true);
    const rows = buildSyncRows({
      fields: syncFields,
      budSource: codeDetail,
      dpmSource: getSelectedDpmSource(),
    }).map(row => (row.id === "clearHeight" ? { ...row, dpmClearHeightRange: row.dpmValue } : row));
    const [err, response] = await to(
      buildingService.previewBudSync(selectedProperty?.id, { rows, recordType: "unit", unitId: selectedUnitCode }),
    );
    setSyncLoading(false);
    if (err) {
      await dispatch(refreshFeatureFlagsAfterBudSyncDisabled(err));
      toastMessage("error", getBudSyncErrorMessage(err, ERROR_MESSAGE.INFO));
      return [];
    }
    return normalizeUnitClearHeightPreviewRows(formatRefreshedSyncRows(response?.rows, syncFields));
  };

  const getLegacySyncRows = () =>
    buildSyncRows({
      fields: syncFields.filter(field => legacyUnitSyncFieldIds.includes(field.key)),
      budSource: codeDetail,
      dpmSource: getSelectedDpmSource(),
    });

  const getLegacySource = () =>
    getLegacySyncRows().reduce(
      (source, row) => ({
        ...source,
        [row.id]: {
          label: row.fieldName,
          value: getLegacyBudValue(row),
        },
      }),
      {},
    );

  const getLegacyCurrent = () =>
    getLegacySyncRows().reduce(
      (current, row) => ({
        ...current,
        [row.id]: getLegacyDpmValue(row),
      }),
      {},
    );

  const _onLegacySuccess = values => {
    if (!isFeatureFlagResolved) return;
    const selectedRows = getLegacySyncRows()
      .filter(row => Object.prototype.hasOwnProperty.call(values, row.id))
      .map(row => ({ ...row, updatedValue: values[row.id] }));

    form.setFieldsValue({
      unitInfo: applyUnitRowsToFormInfo(form.getFieldValue("unitInfo"), index, selectedUnitCode, selectedRows),
    });
    selectedRows.forEach(row => {
      if (notAvailable.unitInfo?.[index]?.[row.path]) updateNotAvailableFields(row.path, index);
    });
    _onCancel();
  };

  const _onApplyRows = async rows => {
    if (!isFeatureFlagResolved || !isBudSyncEnabled) return;
    setSyncLoading(true);
    const updates = rows.flatMap(getUnitClearHeightUpdates);
    const [err] = await to(
      buildingService.applyBudSync(selectedProperty?.id, {
        recordType: "unit",
        unitId: selectedUnitCode,
        updates,
      }),
    );
    setSyncLoading(false);
    if (err) {
      await dispatch(refreshFeatureFlagsAfterBudSyncDisabled(err));
      toastMessage("error", getBudSyncErrorMessage(err, ERROR_MESSAGE.EDITED));
      return;
    }

    form.setFieldsValue({
      unitInfo: applyUnitRowsToFormInfo(form.getFieldValue("unitInfo"), index, selectedUnitCode, rows),
    });
    rows.forEach(row => {
      if (notAvailable.unitInfo?.[index]?.[row.path]) updateNotAvailableFields(row.path, index);
    });
    setIsDifferenceModalOpen(false);
    setCodeDetail({});
    setSelected({});
    toastMessage("success", SUCCESS_MESSAGE.EDITED);
  };

  const shouldBeDisabled = (unitDetail, selectedUnit) =>
    !isFeatureFlagResolved ||
    viewOnlyMode ||
    (isBudSyncEnabled ? _isEmpty(unitDetail) : !hasSyncableUnitSourceDetails(unitDetail)) ||
    _isEmpty(selectedUnit);

  const renderClearHeight = (unitId, fallbackValue) => {
    if (!dimensionDisplayProps) return fallbackValue.value;
    const { buildingUnitOfMeasure, dimensions, dimensionsError, dimensionsLoading, selectedDimensionUnitSystem } =
      dimensionDisplayProps;
    if (dimensionsLoading) return <Skeleton.Input active size="small" />;

    // The saved DPM unit value is authoritative for this panel. The dimensions
    // endpoint can return a formatted value from a different source/UOM, which
    // may not match the value the user just saved.
    const savedClearHeight = formatSavedClearHeight(
      fallbackValue.source,
      buildingUnitOfMeasure,
      selectedDimensionUnitSystem,
      null,
    );
    if (savedClearHeight !== null) return savedClearHeight;

    const savedRange = formatClearHeightRange(fallbackValue.source, buildingUnitOfMeasure, selectedDimensionUnitSystem);
    if (savedRange) return savedRange;

    if (dimensionsError) {
      return formatSavedClearHeight(
        fallbackValue.source,
        buildingUnitOfMeasure,
        selectedDimensionUnitSystem,
        fallbackValue.value || "-",
      );
    }

    const unitDimensions = findUnitDimensions(dimensions, unitId);
    const selectedBlock = getSelectedDimensionBlock(unitDimensions?.clearHeight, selectedDimensionUnitSystem);
    if (!selectedBlock?.formatted) {
      const formattedRange = formatDimensionRange(
        unitDimensions?.minimumClearHeight,
        unitDimensions?.maximumClearHeight,
        selectedDimensionUnitSystem,
      );
      if (formattedRange) return formattedRange;
      return formatSavedClearHeight(
        fallbackValue.source,
        buildingUnitOfMeasure,
        selectedDimensionUnitSystem,
        fallbackValue.value || "-",
      );
    }

    return (
      <span aria-label={getDimensionAriaLabel(unitDimensions.clearHeight, selectedDimensionUnitSystem)}>
        {selectedBlock.formatted}
      </span>
    );
  };

  const getDataSource = () =>
    _reduce(
      _unitCodes,
      (result, _unitCode) => {
        const unitDetail = _find(selectedPropertyUnits, { unitId: _unitCode.value });
        const savedUnit = _find(selectedProperty?.unitInfo, { unitId: _unitCode.value });
        const selectedUnit = getCurrentUnitFormDetails(form, index, savedUnit);
        const formUnit =
          _find(form.getFieldValue("unitInfo"), unit => unitContainsCode(unit, _unitCode.value)) || selectedUnit;
        const unitCharacteristics = _get(unitDetail, "characteristics", {});

        if (isBudSyncEnabled) {
          const characteristicRows = buildCharacteristicsRows({ fields: syncFields, source: formUnit || {} });
          characteristicRows.forEach(row => {
            const recordIndex = result.records.findIndex(record => record.key === row.key);
            if (recordIndex !== -1) result.records[recordIndex][_unitCode.value] = row.value;
          });
        } else {
          result.records.forEach((record, recordIndex) => {
            result.records[recordIndex][_unitCode.value] =
              record.key === "leaseEndDate"
                ? getLeaseEndDateValue(getUnitLeaseEndDate(unitDetail)) || "-"
                : _get(unitCharacteristics, record.key, "-");
          });
        }

        const clearHeightRecordIndex = result.records.findIndex(record => record.key === "clearHeight");
        if (clearHeightRecordIndex !== -1) {
          result.records[clearHeightRecordIndex][_unitCode.value] = renderClearHeight(_unitCode.value, {
            source: isBudSyncEnabled ? formUnit : selectedUnit,
            value: result.records[clearHeightRecordIndex][_unitCode.value],
          });
        }

        const syncUnit = isBudSyncEnabled ? formUnit : selectedUnit;
        const _disabled = shouldBeDisabled(unitDetail, syncUnit);
        const syncButton = (
          <Button
            disabled={_disabled}
            size="small"
            shape="circle"
            type="primary"
            icon={<CustomIcon name="SyncOutlined" />}
            onClick={() => _onClickSyncModal(unitDetail, syncUnit)}
          />
        );

        result.columns.push({
          title: (
            <div className="single-unit-characteristic-heading">
              <Title level={5} ellipsis title={getUnitLabel(_unitCode.label)}>
                {getUnitLabel(_unitCode.label)}
              </Title>
              {isBudSyncEnabled ? <Tooltip title={t("TransferTable.SyncWithBud")}>{syncButton}</Tooltip> : syncButton}
            </div>
          ),
          dataIndex: _unitCode.value,
          key: _unitCode.value,
          width: 150,
        });
        return result;
      },
      {
        records: isBudSyncEnabled
          ? syncFields.map(field => ({ key: field.key, characteristic: field.label }))
          : getLegacyCharacteristicRows(t),
        columns: [
          {
            title: t("UnitCharacteristics.Title"),
            width: 200,
            dataIndex: "characteristic",
            key: "characteristic",
            fixed: "left",
          },
        ],
      },
    );

  const renderTable = () => {
    const dataSource = getDataSource();
    return (
      <Table
        dataSource={dataSource.records}
        columns={dataSource.columns}
        pagination={false}
        scroll={{ x: `${dataSource.columns.length * 120 + 80}px` }}
      />
    );
  };

  return (
    <div className="unit-characteristics-container">
      {isBudSyncEnabled ? (
        <Form.Item noStyle shouldUpdate>
          {renderTable}
        </Form.Item>
      ) : (
        renderTable()
      )}
      <AppModal
        title={t("UnitCharacteristics.SyncLatestChanges")}
        width={900}
        visible={isDifferenceModalOpen}
        onCancel={_onCancel}
        footer={null}
        maxHeight="80vh"
        overFlowY="auto"
        destroyOnClose
      >
        <TransferTable
          key={isBudSyncEnabled ? "bud-sync" : "legacy"}
          onCancel={_onCancel}
          loading={syncLoading || !isFeatureFlagResolved}
          {...(isBudSyncEnabled
            ? { onPreview: getPreviewRows, onApplyRows: _onApplyRows }
            : { source: getLegacySource(), current: getLegacyCurrent(), onSuccess: _onLegacySuccess })}
        />
      </AppModal>
    </div>
  );
};

UnitCharacteristics.propTypes = {
  _unitCodes: PropTypes.array,
  selectedPropertyUnits: PropTypes.array,
  selectedProperty: PropTypes.object,
  form: PropTypes.object,
  notAvailable: PropTypes.object,
  updateNotAvailableFields: PropTypes.func,
  index: PropTypes.number,
  viewOnlyMode: PropTypes.bool,
  dimensionDisplayProps: PropTypes.object,
};

export default UnitCharacteristics;
